import React, { useEffect, useState } from "react";
import { db, auth } from "../../firebase";
import { collection, addDoc, getDocs, updateDoc, doc, query, where, getDoc } from "firebase/firestore";
import Layout from "../../components/Layout";
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

const Pickups = () => {
  const [userRole, setUserRole] = useState(null);
  const [userSupplier, setUserSupplier] = useState(null);
  const [scheduledPickups, setScheduledPickups] = useState([]);
  const [lastPickup, setLastPickup] = useState(null);
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState(""); // ✅ Added time selection
  const [adminNewDate, setAdminNewDate] = useState("");
  const [adminNewTime, setAdminNewTime] = useState(""); // ✅ Admin suggests a new time

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserRole(userSnap.data().role);
          setUserSupplier(userSnap.data().supplier || null);
        }
      }
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    const fetchPickups = async () => {
      try {
        let pickupQuery = collection(db, "pickups");

        if (userRole === "supplier" && userSupplier) {
          pickupQuery = query(pickupQuery, where("supplier", "==", userSupplier));
        }

        const querySnapshot = await getDocs(pickupQuery);
        const pickups = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        setScheduledPickups(pickups);
        if (pickups.length > 0) {
          setLastPickup(pickups[pickups.length - 1].date);
        }
      } catch (error) {
        console.error("Error fetching pickups:", error);
      }
    };

    if (userRole) fetchPickups();
  }, [userRole, userSupplier]);

  const schedulePickup = async () => {
    if (!pickupDate || !pickupTime) {
      alert("Please select both a date and time.");
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "pickups"), {
        supplier: userSupplier,
        date: pickupDate,
        time: pickupTime, // ✅ Save the selected time
        status: "Pending",
      });
      alert("Pickup scheduled!");
      setPickupDate("");
      setPickupTime("");
      setScheduledPickups([...scheduledPickups, { id: docRef.id, supplier: userSupplier, date: pickupDate, time: pickupTime, status: "Pending" }]);
    } catch (error) {
      console.error("Error scheduling pickup:", error);
    }
  };

  const confirmPickup = async (pickupId) => {
    try {
      const pickupRef = doc(db, "pickups", pickupId);
      await updateDoc(pickupRef, { status: "Confirmed" });
      setScheduledPickups(scheduledPickups.map((p) => (p.id === pickupId ? { ...p, status: "Confirmed" } : p)));
    } catch (error) {
      console.error("Error confirming pickup:", error);
    }
  };

  const suggestNewPickup = async (pickupId) => {
    if (!adminNewDate || !adminNewTime) {
      alert("Please select both a new date and time.");
      return;
    }

    try {
      const pickupRef = doc(db, "pickups", pickupId);
      await updateDoc(pickupRef, { date: adminNewDate, time: adminNewTime, status: "New Date Suggested" });

      setScheduledPickups(
        scheduledPickups.map((p) =>
          p.id === pickupId ? { ...p, date: adminNewDate, time: adminNewTime, status: "New Date Suggested" } : p
        )
      );

      alert("New pickup date & time suggested!");
      setAdminNewDate("");
      setAdminNewTime("");
    } catch (error) {
      console.error("Error suggesting new pickup date:", error);
    }
  };

  return (
    <Layout>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h7" sx={{ mb: 3 }}>
          Pickups
        </Typography>

        {/* Supplier View */}
        {userRole === "supplier" && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6">Last Pickup: {lastPickup || "No pickups yet"}</Typography>

            <Typography variant="h6" sx={{ mt: 2 }}>
              Schedule a New Pickup
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="date"
                  label="Pickup Date"
                  value={pickupDate}
                  onChange={(e) => setPickupDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Pickup Time</InputLabel>
                  <Select
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                  >
                    <MenuItem value="08:00 AM">08:00 AM</MenuItem>
                    <MenuItem value="10:00 AM">10:00 AM</MenuItem>
                    <MenuItem value="12:00 PM">12:00 PM</MenuItem>
                    <MenuItem value="02:00 PM">02:00 PM</MenuItem>
                    <MenuItem value="04:00 PM">04:00 PM</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button variant="contained" onClick={schedulePickup}>
                  Schedule Pickup
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Admin & Supplier Shared View */}
        <Typography variant="h6">Scheduled Pickups</Typography>
        <Paper sx={{ p: 3 }}>
          <ul>
            {scheduledPickups.map((pickup) => (
              <li key={pickup.id}>
                {pickup.supplier} - {pickup.date} at {pickup.time || "N/A"} -{" "}
                <strong>{pickup.status}</strong>

                {userRole === "admin" && pickup.status !== "Confirmed" && (
                  <>
                    <Button sx={{ ml: 2 }} variant="contained" color="success" onClick={() => confirmPickup(pickup.id)}>
                      Confirm
                    </Button>

                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={6}>
                        <TextField
                          type="date"
                          label="New Date"
                          value={adminNewDate}
                          onChange={(e) => setAdminNewDate(e.target.value)}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          type="time"
                          label="New Time"
                          value={adminNewTime}
                          onChange={(e) => setAdminNewTime(e.target.value)}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>

                    <Button sx={{ mt: 2 }} variant="outlined" color="warning" onClick={() => suggestNewPickup(pickup.id)}>
                      Suggest New Date & Time
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </Paper>
      </Box>
    </Layout>
  );
};

export default Pickups;
